import { AbstractProgram, AbstractSession } from "@kvix/shared";
import { Button, Text, UserAvatarListCards } from "@kvix/ui";
import { Box, Chip, Collapse, Grid, makeStyles } from "@material-ui/core";
import StarIcon from "@material-ui/icons/Star";
import { addDays } from "date-fns";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled from "styled-components";

const useStyles = makeStyles((theme) => ({
  chip: {
    "&.MuiChip-root": {
      background: theme.palette.background.paper,
      borderRadius: 2,
      userSelect: "none",
      height: 22,

      "& .MuiChip-label": {
        paddingLeft: 6,
        paddingRight: 6,
        fontSize: 13,
      },
    },
  },
}));

interface Props {
  program: AbstractProgram;
  replays: AbstractSession[];
}

const PartnerTitle: React.FC = (props) => (
  <Text
    variant="body1"
    style={{ color: "#cdcecf", textTransform: "uppercase", fontWeight: 700 }}
  >
    <span style={{ color: "#ff0000", marginRight: 4 }}>•</span>
    {props.children}
  </Text>
);

const getTotalBookedUsers = (
  program: AbstractProgram
): AbstractProgram["bookedUsersSummary"] => {
  if (program.bookedUsersSummary) {
    return program.bookedUsersSummary;
  }

  return {
    total: 0,
    highlighted: [],
  };
};

const RatingIcon = styled(StarIcon)`
  fill: #fecb00;
  margin-bottom: -4px;
  margin-left: 2px;
`;

export const ProgramDetails: React.FC<Props> = (props) => {
  const { instructor } = props.program;
  const bookings = getTotalBookedUsers(props.program);
  const { t, ready } = useTranslation(["program", "general", "cards"]);
  const duration = `${props.program.duration || "???"} ${t("minutesSuffix")}`;
  const level = t(`levels.${props.program.level}`);
  const rating = props.program.rating || null;
  const classes = useStyles(props);
  const [disableBookAClass, setDisableBookAClass] = useState<boolean>(false);

  const setButtonState = () => {
    const currentDate = new Date();
    const newDate = addDays(currentDate, 7);
    const sessions = props.program.sessions.filter((sess) => {
      const tmpDate = new Date(sess.start);
      return (
        tmpDate.getTime() >= currentDate.getTime() &&
        tmpDate.getTime() <= newDate.getTime()
      );
    });
    setDisableBookAClass(sessions.length === 0);
  };

  useEffect(() => {
    setButtonState();
  }, [props.program.sessions]);

  if (!ready) {
    return null;
  }

  return (
    <Grid container direction="column" spacing={3}>
      <Grid item>
        <Grid container direction="column" spacing={2}>
          <Grid item container>
            <Chip
              label={level}
              className={classes.chip}
              style={{
                marginRight: 4,
              }}
            />
            <Chip label={duration} className={classes.chip} />
          </Grid>
          {instructor.partnerProfile && (
            <Grid style={{ paddingBottom: 0 }} item>
              <PartnerTitle>{instructor.partnerProfile.title}</PartnerTitle>
            </Grid>
          )}
          <Grid style={{ paddingTop: 0 }} item>
            <Text variant="h1">{props.program.title}</Text>
          </Grid>

          <Grid item>
            <Link to={`/instructor/${instructor.id}`}>
              <Text style={{ lineHeight: 1 }}>
                {t("details.withInstructor", {
                  firstName: instructor.name.first,
                  lastName: instructor.name.last,
                })}
              </Text>
            </Link>
          </Grid>
        </Grid>
      </Grid>

      <Grid style={{ maxWidth: 220 }} item>
        <Box display="flex" justifyContent={"space-between"}>
          {bookings.total > 0 && (
            <UserAvatarListCards
              total={bookings.total}
              users={bookings.highlighted}
            />
          )}
          <Box display="flex" alignItems="center">
            <Text style={{ fontWeight: 600 }}>{rating}</Text>
            <span>{rating && <RatingIcon />}</span>
          </Box>
        </Box>
      </Grid>

      <Grid item>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            const sessionBookerEl = document.getElementById("sessionBooker");
            if (sessionBookerEl)
              sessionBookerEl.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
          }}
          disabled={disableBookAClass}
        >
          {t("general:buttons.bookAClass")}
        </Button>
      </Grid>

      <Grid item>
        <Collapse in={props.replays?.length > 0}>
          <Text
            color="primary"
            style={{ fontWeight: 500, cursor: "pointer" }}
            onClick={() => {
              const latestSessionElement =
                document.getElementById("latestSession");
              if (latestSessionElement)
                latestSessionElement.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
            }}
          >
            {t("details.watchLatestReplay")}
          </Text>
        </Collapse>
      </Grid>
    </Grid>
  );
};
